import React, { ReactNode, useEffect, useState } from 'react';

import { InternalQuestion, InternalQuestionGroups, PreloadedData } from './SubmissionContext';

export type QuestionValidationRule = {
  error: boolean;
  msg: string;
};

type QuestionContextType = {
  questionGroup: InternalQuestionGroups;
  questions?: InternalQuestion[];
  activeQuestion?: InternalQuestion;
  answerQuestion: (question: InternalQuestion, answers: any) => void;
  answerActiveQuestion: (answers: any) => void;
  activateFirstQuestion: () => InternalQuestion;
  activateNextQuestion: () => boolean;
  reEvaluateDisplayRules: () => void;
  evalValidationRule: (question: InternalQuestion) => QuestionValidationRule;
  evalDisplayRule: (question: InternalQuestion) => boolean;
};

export const QuestionContext = React.createContext(undefined as QuestionContextType);

interface QuestionProviderProps {
  questionGroup: InternalQuestionGroups;
  isEditingSubmission: boolean;
  children: ReactNode;
  preloadedData?: PreloadedData;
}

export const QuestionProvider = ({
  questionGroup,
  isEditingSubmission,
  children,
  preloadedData,
}: QuestionProviderProps) => {
  const [questions, setQuestions] = useState<InternalQuestion[] | undefined>();

  const activateQuestion = (question: InternalQuestion) => {
    questions?.forEach((q) => (q.active = false));
    question.active = true;
    question.visible = true;
    setQuestions([...questions]);
  };

  const evalDisplayRule = (question: InternalQuestion) => {
    if (!question.displayRule) {
      return true;
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-implied-eval
      const deserializedDisplayRule = new Function('return ' + question.displayRule)();
      const questions = questionGroup?.questions?.map((question) => {
        const { tempAnswer } = question;
        return { ...question, answer: tempAnswer };
      });
      return deserializedDisplayRule({ ...questionGroup, questions });
    } catch {
      return false;
    }
  };

  const evalValidationRule = (question: InternalQuestion): QuestionValidationRule => {
    if (!question.validationRule) {
      return {
        error: false,
        msg: '',
      };
    }

    try {
      // eslint-disable-next-line @typescript-eslint/no-implied-eval
      const deserializedValidationRule = new Function('return ' + question.validationRule)();
      return deserializedValidationRule([question.tempAnswer]);
    } catch (e) {
      // In case of error, mark as valid
      console.log('Error with question validation rule', e);
      return {
        error: false,
        msg: '',
      };
    }
  };

  const activateNextQuestion = () => {
    const alreadyPassedThrowIt = (key: string) => {
      const lastActiveIndex = questions.findIndex((q: any) => q.key === activeQuestion?.key);
      const currentIndex = questions.findIndex((q: any) => q.key === key);
      return currentIndex > lastActiveIndex;
    };
    const hasPreloadedAnswer = (key: string) => {
      if (preloadedData?.answers && key in preloadedData.answers && alreadyPassedThrowIt(key)) {
        return true;
      }
      return false;
    };
    const items = questions?.filter(
      (q) => (q.tempAnswer === undefined || hasPreloadedAnswer(q.key)) && evalDisplayRule(q),
    );
    items && items.length > 0 && activateQuestion(items[0]);
    return items?.length > 0;
  };

  const activateFirstQuestion = () => {
    activateQuestion(questions?.[0]);
    return questions?.[0];
  };

  const answerQuestion = (question: InternalQuestion, answers: any) => {
    question.tempAnswer = answers;
    setQuestions([...questions]);
  };

  const reEvaluateDisplayRules = () => {
    let lastVisible: InternalQuestion;
    questions?.forEach((q) => {
      q.visible = evalDisplayRule(q);
      if (q.visible) {
        lastVisible = q;
      } // Save last visible question
    });

    if (lastVisible !== activeQuestion) {
      activateQuestion(lastVisible);
    }

    setQuestions([...questions]);
  };

  const answerActiveQuestion = (answers: any) => {
    if (!activeQuestion) {
      return;
    }

    answerQuestion(activeQuestion, answers);
  };

  const activeQuestion = questions?.filter((q) => q.active)?.[0];

  useEffect(() => {
    const questions = questionGroup?.questions;
    if (questions?.[0]) {
      questions[0].active = true;
      questions[0].visible = true;
    }

    if (isEditingSubmission) {
      questions?.forEach((q) => (q.visible = evalDisplayRule(q)));
    }

    setQuestions(questions);
  }, [questionGroup, isEditingSubmission]);

  return (
    <QuestionContext.Provider
      value={{
        activeQuestion,
        activateFirstQuestion,
        activateNextQuestion,
        answerActiveQuestion,
        answerQuestion,
        questionGroup,
        questions,
        reEvaluateDisplayRules,
        evalValidationRule,
        evalDisplayRule,
      }}
    >
      {children}
    </QuestionContext.Provider>
  );
};

export function useQuestionContext() {
  return React.useContext(QuestionContext);
}
